<script setup lang="ts">
import { computed } from "vue";
import { CssVarColors } from "@/shared/themes/CssVarColors.interface";
import { PageBlock } from "@/views/blocks/PageBlock.interface";
import { TwoColumnsWithPhotoRightProps } from "./cta.interfaces";
import EditableElement from "@/views/page/EditableElement.vue";

import Section from "@/views/blocks/shared/section/layout/section/Section.vue";
import FullSpacing from "@/views/blocks/shared/section/layout/spacing/FullSpacing.vue";
import Heading from "@/views/blocks/shared/section/typography/heading/Heading.vue";
import Paragraph from "@/views/blocks/shared/section/typography/paragraph/Paragraph.vue";
import SecondaryButton from "@/views/blocks/shared/section/buttons/secondary-button/SecondaryButton.vue";
import Media from "@/views/blocks/shared/section/media/media/Media.vue";
import List from "./components/list/List.vue";
import type { ScrollAnimationType } from "@/apps/page/page-project.interface";

const props = defineProps<{
    block: PageBlock<TwoColumnsWithPhotoRightProps>;
    pageColors: CssVarColors;
    scrollAnimationType?: ScrollAnimationType;
}>();

const dataValues = computed(() => props.block.dataValues);
const styleValues = computed(() => props.block.styleValues);
</script>

<template>
    <Section
        :id="block.webId"
        :background-color="styleValues.background"
        :scrollAnimationType="scrollAnimationType"
    >
        <FullSpacing>
            <div class="max-w-2xl lg:max-w-7xl mx-auto">
                <div
                    class="px-6 py-16 md:px-20 md:py-20 rounded-3xl shadow-sm flex flex-col lg:flex-row gap-10 lg:gap-20 justify-between gradient-background"
                >
                    <div class="flex flex-col justify-center gap-y-6 grow">
                        <EditableElement
                            v-if="!dataValues.title.isHidden"
                            :block-id="block.id"
                            field-name="title"
                        >
                            <Heading
                                :tag="dataValues.title.data.tag"
                                :text="dataValues.title.data.text"
                                :text-color="styleValues.text"
                                class="text-4xl sm:text-5xl"
                            />
                        </EditableElement>

                        <EditableElement
                            v-if="!dataValues.description.isHidden"
                            :block-id="block.id"
                            field-name="description"
                        >
                            <Paragraph
                                :text="dataValues.description.data.text"
                                :text-color="styleValues.text"
                                class="text-lg/8 opacity-70"
                            />
                        </EditableElement>

                        <List
                            v-if="dataValues.listItems.data.items.length > 0"
                            :block-id="block.id"
                            :items="dataValues.listItems.data.items"
                            :text-color="styleValues.text"
                            :checkmark-color="styleValues.brand"
                        />

                        <EditableElement
                            v-if="!dataValues.link.isHidden"
                            :block-id="block.id"
                            field-name="link"
                        >
                            <SecondaryButton
                                :label="dataValues.link.data.label"
                                :href="dataValues.link.data.href"
                                :text-color="styleValues.brand"
                                class="w-fit"
                            />
                        </EditableElement>
                    </div>
                    <div class="flex flex-col justify-center">
                        <EditableElement
                            v-if="!dataValues.image.isHidden"
                            :block-id="block.id"
                            field-name="image"
                        >
                            <div
                                class="h-full max-h-96 w-full aspect-square lg:aspect-auto lg:w-auto lg:max-w-sm rounded-lg overflow-hidden"
                            >
                                <Media
                                    :data="dataValues.image.data"
                                    :image-width="800"
                                    :image-height="475"
                                    :video-width="800"
                                    :video-height="475"
                                />
                            </div>
                        </EditableElement>
                    </div>
                </div>
            </div>
        </FullSpacing>
    </Section>
</template>

<style scoped>
.gradient-background {
    background: linear-gradient(
        to right,
        color-mix(in srgb, transparent 95%, v-bind("styleValues.brand")),
        color-mix(in srgb, transparent 80%, v-bind("styleValues.brand"))
    );
}
</style>
